import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faSearch,
  faFilter,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faUserTag,
  faShield,
  faCrown,
  faUserCheck,
  faTimes,
  faClock,
  faSync,
  faCreditCard,
  faExclamationTriangle,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import "../../css/Common.css";
import "./css/UserAdmin.css";
import { useRoleCheck } from "../../../../hooks/useRoleCheck";
import APIService from "../../services/APIService";
import LoadingSpinner from "../../common/LoadingSpinner";
import {
  formatDateTime,
  formatDateOnly,
  formatTimeOnly,
} from "../../utils/DateTimeUtil";

function UserAdmin() {
  const { userRoles, isLoading: rolesLoading } = useRoleCheck();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Login history popover state
  const [loginHistoryPopover, setLoginHistoryPopover] = useState({
    open: false,
    userUuid: null,
    userName: "",
    logins: [],
    loading: false,
    error: null,
  });

  // Subscription collapsible panel state
  const [subscriptionPanel, setSubscriptionPanel] = useState({
    expanded: false,
    data: null,
    loading: false,
    error: null,
    loaded: false,
  });

  // Org & Roles collapsible panel state
  const [orgRolesPanel, setOrgRolesPanel] = useState({
    expanded: false,
  });

  // Scam reports collapsible panel state
  const [scamReportsPanel, setScamReportsPanel] = useState({
    expanded: false,
    data: null,
    loading: false,
    error: null,
    loaded: false,
  });
  const popoverRef = useRef(null);
  const popoverTriggerRef = useRef(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!loginHistoryPopover.open) return;

    const handleClickOutside = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        popoverTriggerRef.current &&
        !popoverTriggerRef.current.contains(e.target)
      ) {
        setLoginHistoryPopover((prev) => ({ ...prev, open: false }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [loginHistoryPopover.open]);

  /**
   * Fetch and show login history popover for a user
   */
  const handleLoginHistoryClick = useCallback(
    async (e, user) => {
      e.stopPropagation();

      // Toggle off if clicking the same user
      if (
        loginHistoryPopover.open &&
        loginHistoryPopover.userUuid === user.uuid
      ) {
        setLoginHistoryPopover((prev) => ({ ...prev, open: false }));
        return;
      }

      // Store the trigger element ref
      popoverTriggerRef.current = e.currentTarget;

      setLoginHistoryPopover({
        open: true,
        userUuid: user.uuid,
        userName: `${user.firstName} ${user.lastName}`,
        logins: [],
        loading: true,
        error: null,
      });

      try {
        const data = await APIService.get(
          `/api/v1/base/roles/login-history/${user.uuid}`,
        );
        setLoginHistoryPopover((prev) => ({
          ...prev,
          logins: data.logins || [],
          loading: false,
        }));
      } catch (err) {
        setLoginHistoryPopover((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load login history",
        }));
      }
    },
    [loginHistoryPopover.open, loginHistoryPopover.userUuid],
  );

  const fetchSubscription = useCallback(async (userUuid) => {
    setSubscriptionPanel((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await APIService.get(
        `/api/v1/admin/dashboard/user-subscription/${userUuid}`,
      );
      setSubscriptionPanel((prev) => ({
        ...prev,
        data,
        loading: false,
        loaded: true,
      }));
    } catch (err) {
      setSubscriptionPanel((prev) => ({
        ...prev,
        loading: false,
        loaded: false,
        error: "Failed to load subscription data",
      }));
    }
  }, []);

  const fetchScamReports = useCallback(async (userUuid) => {
    setScamReportsPanel((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await APIService.get(
        `/api/v1/admin/dashboard/user-scam-reports/${userUuid}`,
      );
      setScamReportsPanel((prev) => ({
        ...prev,
        data,
        loading: false,
        loaded: true,
      }));
    } catch (err) {
      setScamReportsPanel((prev) => ({
        ...prev,
        loading: false,
        loaded: false,
        error: "Failed to load scam reports",
      }));
    }
  }, []);

  const handleToggleSubscription = useCallback(() => {
    setSubscriptionPanel((prev) => {
      const nowExpanded = !prev.expanded;
      if (nowExpanded && !prev.loaded && selectedUser) {
        fetchSubscription(selectedUser.uuid);
      }
      return { ...prev, expanded: nowExpanded };
    });
  }, [selectedUser, fetchSubscription]);

  const handleToggleOrgRoles = useCallback(() => {
    setOrgRolesPanel((prev) => ({ expanded: !prev.expanded }));
  }, []);

  const handleToggleScamReports = useCallback(() => {
    setScamReportsPanel((prev) => {
      const nowExpanded = !prev.expanded;
      if (nowExpanded && !prev.loaded && selectedUser) {
        fetchScamReports(selectedUser.uuid);
      }
      return { ...prev, expanded: nowExpanded };
    });
  }, [selectedUser, fetchScamReports]);

  // Role hierarchy: index 0 = highest
  const ROLE_HIERARCHY = [
    "platform_admin",
    "account_admin",
    "group_admin",
    "account_member",
    "group_member",
    "group_viewer",
    "demo_seat",
  ];

  // Available roles for filtering
  const availableRoles = [
    {
      name: "platform_admin",
      label: "Platform Admin",
      icon: faCrown,
      color: "#dc3545",
    },
    {
      name: "account_admin",
      label: "Account Admin",
      icon: faShield,
      color: "#fd7e14",
    },
    {
      name: "group_admin",
      label: "Group Admin",
      icon: faUserTag,
      color: "#6f42c1",
    },
    {
      name: "account_member",
      label: "Account Member",
      icon: faUserCheck,
      color: "#0d6efd",
    },
    {
      name: "group_member",
      label: "Group Member",
      icon: faUserCheck,
      color: "#198754",
    },
    {
      name: "group_viewer",
      label: "Group Viewer",
      icon: faUsers,
      color: "#6c757d",
    },
    {
      name: "demo_seat",
      label: "Demo Seat",
      icon: faUsers,
      color: "#adb5bd",
    },
  ];

  // Check if current user has platform admin access
  const hasPlatformAdminAccess =
    userRoles && userRoles.includes("platform_admin");

  const fetchUsersWithRoles = async () => {
    if (!hasPlatformAdminAccess) {
      setError("Access denied: Platform admin role required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
      });

      if (selectedRoles.length > 0) {
        // Append each role separately for proper backend parsing
        selectedRoles.forEach((role) => {
          params.append("roles", role);
        });
      }

      if (appliedSearch.trim()) {
        params.append("search", appliedSearch.trim());
      }

      const endpoint = `/api/v1/base/roles/user-roles-admin?${params}`;
      const data = await APIService.get(endpoint);
      setUsers(data.users || []);
      setPagination({
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        hasNext: data.hasNext || false,
        hasPrevious: data.hasPrevious || false,
      });
    } catch (err) {
      console.error("Error fetching users with roles:", err);
      // APIService handles 401 redirects automatically, so we just need to handle other errors
      if (
        err.message.includes("403") ||
        err.message.includes("Access denied")
      ) {
        setError("Access denied: Platform admin role required");
      } else {
        setError(err.message || "Failed to fetch users with roles");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!rolesLoading) {
      fetchUsersWithRoles();
    }
  }, [currentPage, selectedRoles, userRoles, rolesLoading, appliedSearch]);

  useEffect(() => {
    if (!rolesLoading) {
      if (currentPage !== 0) {
        setCurrentPage(0);
      } else {
        fetchUsersWithRoles();
      }
    }
  }, [pageSize]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm.trim());
    setCurrentPage(0);
  };

  const handleRoleToggle = (roleName) => {
    setSelectedRoles((prev) => {
      const newSelectedRoles = prev.includes(roleName)
        ? prev.filter((role) => role !== roleName)
        : [...prev, roleName];
      return newSelectedRoles;
    });
  };

  const clearFilters = () => {
    setSelectedRoles([]);
    setSearchTerm("");
    setAppliedSearch("");
    setCurrentPage(0);
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setLoginHistoryPopover({
      open: false,
      userUuid: null,
      userName: "",
      logins: [],
      loading: false,
      error: null,
    });
    setSubscriptionPanel({
      expanded: false,
      data: null,
      loading: false,
      error: null,
      loaded: false,
    });
    setScamReportsPanel({
      expanded: false,
      data: null,
      loading: false,
      error: null,
      loaded: false,
    });
    setOrgRolesPanel({ expanded: false });
  };

  const handleCloseSideSheet = () => {
    setSelectedUser(null);
    setLoginHistoryPopover((prev) => ({ ...prev, open: false }));
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const getRoleIcon = (roleName) => {
    const role = availableRoles.find((r) => r.name === roleName);
    return role ? role.icon : faUserCheck;
  };

  const getRoleColor = (roleName) => {
    const role = availableRoles.find((r) => r.name === roleName);
    return role ? role.color : "#6c757d";
  };

  const formatRoleName = (roleName) => {
    const role = availableRoles.find((r) => r.name === roleName);
    return role
      ? role.label
      : roleName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleLastLoginSort = () => {
    setSortConfig((prev) => ({
      key: "lastLoginAt",
      direction:
        prev.key === "lastLoginAt" && prev.direction === "desc"
          ? "asc"
          : "desc",
    }));
  };

  const sortedUsers = React.useMemo(() => {
    if (sortConfig.key !== "lastLoginAt") return users;
    return [...users].sort((a, b) => {
      const aVal = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
      const bVal = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [users, sortConfig]);

  const getHighestRole = (roles) => {
    if (!roles || roles.length === 0) return null;
    const sorted = [...roles].sort((a, b) => {
      const ai = ROLE_HIERARCHY.indexOf(a);
      const bi = ROLE_HIERARCHY.indexOf(b);
      const aRank = ai === -1 ? ROLE_HIERARCHY.length : ai;
      const bRank = bi === -1 ? ROLE_HIERARCHY.length : bi;
      return aRank - bRank;
    });
    return sorted[0];
  };

  // Label for the Status column. Demo/test seats surface their own seat status
  // (e.g. "Deactivated", "Expired") so it matches the Test User Management page.
  const getStatusLabel = (user) => {
    if (user.seatStatus && user.seatStatus !== "ACTIVE") {
      return user.seatStatus.charAt(0) + user.seatStatus.slice(1).toLowerCase();
    }
    return user.active ? "Active" : "Inactive";
  };

  if (rolesLoading) {
    return (
      <div className="user-roles-admin">
        <LoadingSpinner message="Loading user roles..." />
      </div>
    );
  }

  if (!hasPlatformAdminAccess) {
    return (
      <div className="user-roles-admin">
        <div className="access-denied">
          <FontAwesomeIcon icon={faShield} className="access-denied-icon" />
          <h3>Access Denied</h3>
          <p>You need Platform Admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-roles-admin">
      <div className="page-header">
        <h1>
          <FontAwesomeIcon icon={faUsers} className="page-icon" />
          User Administration
        </h1>
        <p className="page-description">
          View and search all users and their assigned roles across the platform
        </p>
      </div>

      {/* Search and Filter Section */}
      <div className="controls-section">
        <form onSubmit={handleSearchSubmit} className="search-form">
          <div className="search-input-container">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or UUID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {(searchTerm || appliedSearch) && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setAppliedSearch("");
                }}
                className="clear-search-button"
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="filter-section">
          <div className="filter-header">
            <FontAwesomeIcon icon={faFilter} />
            <span>Filter by Roles (users must have ALL selected roles):</span>
          </div>
          <div className="role-filters">
            {availableRoles.map((role) => (
              <label key={role.name} className="role-filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(role.name)}
                  onChange={() => handleRoleToggle(role.name)}
                />
                <FontAwesomeIcon
                  icon={role.icon}
                  style={{ color: role.color }}
                  className="role-icon"
                />
                <span className="role-label">{role.label}</span>
              </label>
            ))}
          </div>
          {(selectedRoles.length > 0 || appliedSearch) && (
            <button onClick={clearFilters} className="clear-filters-button">
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="results-section">
        {loading ? (
          <LoadingSpinner
            size="sm"
            message="Loading users..."
            className="compact"
          />
        ) : error ? (
          <div className="error-state">
            <p className="error-message">{error}</p>
            <button
              onClick={() => fetchUsersWithRoles()}
              className="retry-button"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="results-header">
              <div className="results-info">
                <span className="results-count">
                  {pagination.totalElements} user
                  {pagination.totalElements !== 1 ? "s" : ""} found
                </span>
                {(selectedRoles.length > 0 || appliedSearch) && (
                  <span className="filter-info">
                    {selectedRoles.length > 0 && (
                      <span>
                        Filtered by:{" "}
                        {selectedRoles
                          .map((role) => formatRoleName(role))
                          .join(", ")}
                      </span>
                    )}
                    {appliedSearch && <span>Search: "{appliedSearch}"</span>}
                  </span>
                )}
              </div>
            </div>

            {users.length === 0 ? (
              <div className="no-results">
                <p>No users found matching your criteria.</p>
              </div>
            ) : (
              <>
                <div className="users-table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Name / Email</th>
                        <th>Roles</th>
                        <th>Status</th>
                        <th
                          className="sortable-th"
                          onClick={handleLastLoginSort}
                          title="Sort by Last Login"
                        >
                          Last Login
                          <span className="sort-icon">
                            {sortConfig.key === "lastLoginAt" ? (
                              <FontAwesomeIcon
                                icon={
                                  sortConfig.direction === "asc"
                                    ? faChevronUp
                                    : faChevronDown
                                }
                              />
                            ) : (
                              <span className="sort-icon-neutral">&#8597;</span>
                            )}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.map((user) => (
                        <tr
                          key={user.uuid}
                          className="user-row"
                          onClick={() => handleRowClick(user)}
                          title="Click to view details"
                        >
                          <td className="user-info">
                            <div className="user-name">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="user-email-compact">
                              {user.email}
                            </div>
                          </td>
                          <td className="roles-info">
                            {(() => {
                              const highest = getHighestRole(user.roles);
                              return highest ? (
                                <span
                                  className="role-badge"
                                  style={{ color: getRoleColor(highest) }}
                                >
                                  <FontAwesomeIcon
                                    icon={getRoleIcon(highest)}
                                  />
                                  {formatRoleName(highest)}
                                </span>
                              ) : (
                                <span className="no-roles">No Roles</span>
                              );
                            })()}
                          </td>
                          <td className="status-info">
                            <div
                              className={`status-indicator ${
                                user.active ? "active" : "inactive"
                              }`}
                            >
                              {getStatusLabel(user)}
                            </div>
                          </td>
                          <td className="last-login-compact">
                            {user.lastLoginAt ? (
                              formatDateOnly(user.lastLoginAt, "short")
                            ) : (
                              <span className="never-logged-in">Never</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="pagination-section">
                  <div className="pagination-left">
                    <div className="pagination-info">
                      {pagination.totalPages > 1
                        ? `Page ${currentPage + 1} of ${pagination.totalPages} · ${pagination.totalElements} users`
                        : `${pagination.totalElements} user${
                            pagination.totalElements !== 1 ? "s" : ""
                          }`}
                    </div>
                    <div className="page-size-selector">
                      <span className="page-size-label">Show:</span>
                      {[20, 50, 100].map((size) => (
                        <button
                          key={size}
                          onClick={() => handlePageSizeChange(size)}
                          className={`page-size-button ${
                            pageSize === size ? "active" : ""
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                  {pagination.totalPages > 1 && (
                    <div className="pagination-controls">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={!pagination.hasPrevious}
                        className="pagination-button"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                        Previous
                      </button>
                      <span className="page-numbers">
                        {Array.from(
                          { length: Math.min(5, pagination.totalPages) },
                          (_, i) => {
                            let pageNumber;
                            if (pagination.totalPages <= 5) {
                              pageNumber = i;
                            } else if (currentPage < 2) {
                              pageNumber = i;
                            } else if (
                              currentPage >
                              pagination.totalPages - 3
                            ) {
                              pageNumber = pagination.totalPages - 5 + i;
                            } else {
                              pageNumber = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setCurrentPage(pageNumber)}
                                className={`page-number ${
                                  currentPage === pageNumber ? "active" : ""
                                }`}
                              >
                                {pageNumber + 1}
                              </button>
                            );
                          },
                        )}
                      </span>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className="pagination-button"
                      >
                        Next
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* User Detail Side Sheet */}
      {selectedUser && (
        <>
          <div className="side-sheet-overlay" onClick={handleCloseSideSheet} />
          <div className="side-sheet-panel">
            <div className="side-sheet-header">
              <div className="side-sheet-title-group">
                <h2 className="side-sheet-title">
                  {selectedUser.firstName} {selectedUser.lastName}
                </h2>
                <div className="side-sheet-uuid">{selectedUser.uuid}</div>
              </div>
              <button
                className="side-sheet-close"
                onClick={handleCloseSideSheet}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="side-sheet-body">
              {/* Contact */}
              <div className="side-sheet-section">
                <div className="side-sheet-section-title">Contact</div>
                <div className="side-sheet-field">
                  <span className="field-label">Email</span>
                  <span className="field-value">{selectedUser.email}</span>
                </div>
                {selectedUser.phone && (
                  <div className="side-sheet-field">
                    <span className="field-label">Phone</span>
                    <span className="field-value">{selectedUser.phone}</span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="side-sheet-section">
                <div className="side-sheet-section-title">Status</div>
                <div className="side-sheet-field">
                  <span className="field-label">Account</span>
                  <div
                    className={`status-indicator ${
                      selectedUser.active ? "active" : "inactive"
                    }`}
                  >
                    {getStatusLabel(selectedUser)}
                  </div>
                </div>
                <div className="side-sheet-field">
                  <span className="field-label">Verified</span>
                  <div
                    className={`verification-indicator ${
                      selectedUser.verified ? "verified" : "unverified"
                    }`}
                  >
                    {selectedUser.verified ? "Verified" : "Unverified"}
                    <span className="verification-breakdown">
                      (Email:{" "}
                      {selectedUser.emailVerified ? "Verified" : "Unverified"},
                      Phone:{" "}
                      {selectedUser.phoneVerified ? "Verified" : "Unverified"})
                    </span>
                  </div>
                </div>
              </div>

              {/* Activity */}
              <div className="side-sheet-section">
                <div className="side-sheet-section-title">Activity</div>
                <div className="side-sheet-field">
                  <span className="field-label">Last Login</span>
                  <span className="field-value">
                    {selectedUser.lastLoginAt ? (
                      <div
                        className="last-login-clickable"
                        onClick={(e) =>
                          handleLoginHistoryClick(e, selectedUser)
                        }
                        title="Click to view recent login history"
                      >
                        <div className="last-login-date">
                          {formatDateOnly(selectedUser.lastLoginAt, "short")}
                        </div>
                        <div className="last-login-time">
                          {formatTimeOnly(selectedUser.lastLoginAt, true)}
                          <FontAwesomeIcon
                            icon={faClock}
                            className="login-history-icon"
                          />
                        </div>
                        {/* Login History Popover */}
                        {loginHistoryPopover.open &&
                          loginHistoryPopover.userUuid ===
                            selectedUser.uuid && (
                            <div
                              className="login-history-popover"
                              ref={popoverRef}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="login-history-popover-header">
                                <span className="login-history-popover-title">
                                  <FontAwesomeIcon icon={faClock} />
                                  Recent Logins
                                </span>
                                <button
                                  className="login-history-popover-close"
                                  onClick={() =>
                                    setLoginHistoryPopover((prev) => ({
                                      ...prev,
                                      open: false,
                                    }))
                                  }
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </div>
                              <div className="login-history-popover-name">
                                {loginHistoryPopover.userName}
                              </div>
                              <div className="login-history-popover-body">
                                {loginHistoryPopover.loading ? (
                                  <div className="login-history-loading">
                                    <LoadingSpinner
                                      size="sm"
                                      message="Loading..."
                                      className="compact"
                                    />
                                  </div>
                                ) : loginHistoryPopover.error ? (
                                  <div className="login-history-error">
                                    {loginHistoryPopover.error}
                                  </div>
                                ) : loginHistoryPopover.logins.length === 0 ? (
                                  <div className="login-history-empty">
                                    No login records found
                                  </div>
                                ) : (
                                  <ul className="login-history-list">
                                    {loginHistoryPopover.logins.map(
                                      (login, idx) => (
                                        <li
                                          key={idx}
                                          className="login-history-item"
                                        >
                                          <span className="login-history-item-date">
                                            {formatDateOnly(
                                              login.loginTimestamp,
                                              "short",
                                            )}
                                          </span>
                                          <span className="login-history-item-time">
                                            {formatTimeOnly(
                                              login.loginTimestamp,
                                              true,
                                            )}
                                          </span>
                                          {login.ipAddress && (
                                            <span className="login-history-item-ip">
                                              {login.ipAddress}
                                            </span>
                                          )}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    ) : (
                      <span className="never-logged-in">Never</span>
                    )}
                  </span>
                </div>
                <div className="side-sheet-field">
                  <span className="field-label">Created</span>
                  <span className="field-value">
                    {selectedUser.createdAt
                      ? formatDateOnly(selectedUser.createdAt, "short")
                      : "N/A"}
                  </span>
                </div>
              </div>

              {/* Organization & Roles - collapsible */}
              <div className="side-sheet-section side-sheet-collapsible-section">
                <div
                  className="side-sheet-collapsible-header"
                  onClick={handleToggleOrgRoles}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleToggleOrgRoles()}
                >
                  <div className="side-sheet-collapsible-title">
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="collapsible-section-icon"
                    />
                    Organization &amp; Roles
                  </div>
                  <div className="collapsible-header-right">
                    <FontAwesomeIcon
                      icon={
                        orgRolesPanel.expanded ? faChevronUp : faChevronDown
                      }
                      className="collapsible-chevron"
                    />
                  </div>
                </div>
                {orgRolesPanel.expanded && (
                  <div className="collapsible-content">
                    <div className="side-sheet-field">
                      <span className="field-label">Account</span>
                      <span className="field-value">
                        {selectedUser.accountName ? (
                          <>
                            {selectedUser.accountName}
                            <span className="field-sub">
                              {" "}
                              ID: {selectedUser.accountId}
                            </span>
                          </>
                        ) : (
                          <em className="no-account">None</em>
                        )}
                      </span>
                    </div>
                    <div className="side-sheet-field">
                      <span className="field-label">Group</span>
                      <span className="field-value">
                        {selectedUser.groupName ? (
                          selectedUser.groupName
                        ) : (
                          <em className="no-group">None</em>
                        )}
                      </span>
                    </div>
                    <div className="side-sheet-field">
                      <span className="field-label">Roles</span>
                      <div className="roles-list side-sheet-roles">
                        {selectedUser.roles && selectedUser.roles.length > 0 ? (
                          selectedUser.roles.map((role, index) => (
                            <span
                              key={index}
                              className="role-badge"
                              style={{ color: getRoleColor(role) }}
                            >
                              <FontAwesomeIcon icon={getRoleIcon(role)} />
                              {formatRoleName(role)}
                            </span>
                          ))
                        ) : (
                          <span className="no-roles">No Roles</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Subscription - collapsible */}
              <div className="side-sheet-section side-sheet-collapsible-section">
                <div
                  className="side-sheet-collapsible-header"
                  onClick={handleToggleSubscription}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleToggleSubscription()
                  }
                >
                  <div className="side-sheet-collapsible-title">
                    <FontAwesomeIcon
                      icon={faCreditCard}
                      className="collapsible-section-icon"
                    />
                    Subscription
                  </div>
                  <div className="collapsible-header-right">
                    {subscriptionPanel.expanded && (
                      <button
                        className="panel-refresh-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchSubscription(selectedUser.uuid);
                        }}
                        title="Refresh subscription data"
                        disabled={subscriptionPanel.loading}
                      >
                        <FontAwesomeIcon
                          icon={faSync}
                          spin={subscriptionPanel.loading}
                        />
                      </button>
                    )}
                    <FontAwesomeIcon
                      icon={
                        subscriptionPanel.expanded ? faChevronUp : faChevronDown
                      }
                      className="collapsible-chevron"
                    />
                  </div>
                </div>
                {subscriptionPanel.expanded && (
                  <div className="collapsible-content">
                    {subscriptionPanel.loading ? (
                      <LoadingSpinner
                        size="sm"
                        message="Loading subscription..."
                        className="compact"
                      />
                    ) : subscriptionPanel.error ? (
                      <div className="panel-error">
                        {subscriptionPanel.error}
                      </div>
                    ) : subscriptionPanel.data?.found ? (
                      <div className="subscription-details">
                        {(() => {
                          const sub = subscriptionPanel.data.subscription;
                          return (
                            <>
                              <div className="side-sheet-field">
                                <span className="field-label">Plan</span>
                                <span className="field-value">
                                  {sub.planName || `Plan ${sub.planId}`}
                                  {sub.isTrial && (
                                    <span className="sub-badge sub-badge-trial">
                                      Trial
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className="side-sheet-field">
                                <span className="field-label">Status</span>
                                <span
                                  className={`sub-status-badge sub-status-${(sub.status || "").toLowerCase()}`}
                                >
                                  {sub.status}
                                </span>
                              </div>
                              <div className="side-sheet-field">
                                <span className="field-label">Plan Price</span>
                                <span className="field-value">
                                  {sub.originalAmount != null
                                    ? `${sub.currency || "USD"} ${Number(sub.originalAmount).toFixed(2)}`
                                    : sub.amount != null
                                      ? `${sub.currency || "USD"} ${Number(sub.amount).toFixed(2)}`
                                      : "—"}{" "}
                                  /{" "}
                                  {sub.paymentCycle
                                    ? sub.paymentCycle.toLowerCase()
                                    : "—"}
                                </span>
                              </div>
                              <div className="side-sheet-field">
                                <span className="field-label">Promo Code</span>
                                <span className="field-value">
                                  {sub.promoCode ? (
                                    <>
                                      <span className="sub-badge sub-badge-promo">
                                        {sub.promoCode}
                                      </span>
                                      {sub.discountPercent != null && (
                                        <span className="field-sub">
                                          {" "}
                                          {sub.discountPercent}% off
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="field-muted">None</span>
                                  )}
                                </span>
                              </div>
                              <div className="side-sheet-field">
                                <span className="field-label">
                                  Effective Price
                                </span>
                                <span className="field-value">
                                  {(() => {
                                    const base =
                                      sub.originalAmount != null
                                        ? sub.originalAmount
                                        : sub.amount;
                                    if (base == null) return "—";
                                    const discount =
                                      sub.discountPercent != null
                                        ? sub.discountPercent
                                        : 0;
                                    const effective =
                                      Number(base) * (1 - discount / 100);
                                    return `${sub.currency || "USD"} ${effective.toFixed(2)}`;
                                  })()}{" "}
                                  /{" "}
                                  {sub.paymentCycle
                                    ? sub.paymentCycle.toLowerCase()
                                    : "—"}
                                </span>
                              </div>
                              {sub.nextBillingDate && (
                                <div className="side-sheet-field">
                                  <span className="field-label">
                                    Next Payment
                                  </span>
                                  <span className="field-value">
                                    {sub.nextBillingDate}
                                  </span>
                                </div>
                              )}
                              {sub.isTrial && sub.trialEndDate && (
                                <div className="side-sheet-field">
                                  <span className="field-label">
                                    Trial Ends
                                  </span>
                                  <span className="field-value">
                                    {sub.trialEndDate}
                                  </span>
                                </div>
                              )}
                              <div className="side-sheet-field">
                                <span className="field-label">Start Date</span>
                                <span className="field-value">
                                  {sub.startDate || "—"}
                                </span>
                              </div>
                              <div className="side-sheet-field">
                                <span className="field-label">Auto-renew</span>
                                <span className="field-value">
                                  {sub.autoRenew ? "Yes" : "No"}
                                </span>
                              </div>
                              {sub.cancelledAt && (
                                <div className="side-sheet-field">
                                  <span className="field-label">Cancelled</span>
                                  <span className="field-value">
                                    {sub.cancelledAt}
                                    {sub.cancelledReason && (
                                      <div className="field-sub">
                                        {sub.cancelledReason}
                                      </div>
                                    )}
                                  </span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="panel-empty">No subscription found</div>
                    )}
                    {selectedUser?.uuid && (
                      <div className="subscription-audit-link-row">
                        <button
                          type="button"
                          className="subscription-audit-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            const target = `User Subscription Audit:UUID:${selectedUser.uuid}`;
                            localStorage.setItem(
                              "requestedHeroContent",
                              target,
                            );
                            window.dispatchEvent(
                              new CustomEvent("heroContentRequested", {
                                detail: target,
                              }),
                            );
                          }}
                          title="Open the full subscription & payment audit timeline for this user"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                          View Full Subscription Audit
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Scam Reports - collapsible */}
              <div className="side-sheet-section side-sheet-collapsible-section">
                <div
                  className="side-sheet-collapsible-header"
                  onClick={handleToggleScamReports}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleToggleScamReports()
                  }
                >
                  <div className="side-sheet-collapsible-title">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="collapsible-section-icon"
                    />
                    Scam Reports
                    {scamReportsPanel.data?.totalElements != null && (
                      <span className="collapsible-count-badge">
                        {scamReportsPanel.data.totalElements}
                      </span>
                    )}
                  </div>
                  <div className="collapsible-header-right">
                    {scamReportsPanel.expanded && (
                      <button
                        className="panel-refresh-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchScamReports(selectedUser.uuid);
                        }}
                        title="Refresh scam reports"
                        disabled={scamReportsPanel.loading}
                      >
                        <FontAwesomeIcon
                          icon={faSync}
                          spin={scamReportsPanel.loading}
                        />
                      </button>
                    )}
                    <FontAwesomeIcon
                      icon={
                        scamReportsPanel.expanded ? faChevronUp : faChevronDown
                      }
                      className="collapsible-chevron"
                    />
                  </div>
                </div>
                {scamReportsPanel.expanded && (
                  <div className="collapsible-content">
                    {scamReportsPanel.loading ? (
                      <LoadingSpinner
                        size="sm"
                        message="Loading scam reports..."
                        className="compact"
                      />
                    ) : scamReportsPanel.error ? (
                      <div className="panel-error">
                        {scamReportsPanel.error}
                      </div>
                    ) : scamReportsPanel.data?.reports?.length > 0 ? (
                      <>
                        <div className="scam-reports-summary">
                          {scamReportsPanel.data.totalElements} total report
                          {scamReportsPanel.data.totalElements !== 1 ? "s" : ""}
                          {scamReportsPanel.data.totalPages > 1 && (
                            <span className="scam-reports-page-note">
                              {" "}
                              · showing latest{" "}
                              {scamReportsPanel.data.reports.length}
                            </span>
                          )}
                        </div>
                        <ul className="scam-reports-list">
                          {scamReportsPanel.data.reports.map((report) => (
                            <li key={report.id} className="scam-report-item">
                              <div className="scam-report-header">
                                <span
                                  className={`scam-risk-badge scam-risk-${(report.riskLevel || "unknown").toLowerCase()}`}
                                >
                                  {report.riskLevel || "UNKNOWN"}
                                </span>
                                <span className="scam-report-type">
                                  {(report.analysisType || "")
                                    .replace(/_/g, " ")
                                    .toLowerCase()
                                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                                </span>
                                {report.isScam && (
                                  <span className="scam-confirmed-badge">
                                    Scam
                                  </span>
                                )}
                              </div>
                              {report.analysisTitle && (
                                <div className="scam-report-title">
                                  {report.analysisTitle}
                                </div>
                              )}
                              <div className="scam-report-meta">
                                {report.createdAt &&
                                  formatDateOnly(report.createdAt, "short")}
                                {report.confidence != null && (
                                  <span className="scam-report-confidence">
                                    {" "}
                                    · {Math.round(report.confidence * 100)}%
                                    confidence
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : scamReportsPanel.data ? (
                      <div className="panel-empty">No scam reports found</div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UserAdmin;
